import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePublicConfig } from "../lib/useConfig";
import api, { formatApiError, track } from "../lib/api";
import { gaEvent } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Check, Upload, FileText, Loader2, Trash2, ShieldCheck,
} from "lucide-react";

const CAT_ICON_ORDER = ["story", "evidence", "revenue", "relationship", "infrastructure"];

/* ---------- Start screen: tier selection + create ---------- */
export function AssessmentStart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const config = usePublicConfig();
  const [tier, setTier] = useState("full");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(false);

  const start = async () => {
    if (!agreed) { toast.error("Please agree to the terms to continue."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/assessments", { tier, email: email || undefined, agreed_terms: agreed });
      if (email) await api.patch(`/assessments/${data.id}`, { profile: { authorized_uploads: authorized } });
      track("assessment_start", data.id);
      gaEvent("assessment_started", { assessment_id: data.id, tier });
      navigate(`/assessment/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const price = config?.pricing?.full_price ?? 79;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
      <span className="text-clay text-xs font-semibold tracking-[0.2em] uppercase">Let's begin</span>
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-ink">Let's take an honest look at where you stand</h1>
      <p className="mt-4 text-ink/75 leading-relaxed">There are no perfect answers, and a lower score doesn't mean your work isn't worth funding. It just helps you see what to strengthen and which funding is realistic right now. Answer based on what's actually in place today — not what you're hoping to build. Your answers save as you go.</p>
      <p className="mt-3 text-sm text-muted-foreground">Pick how far you'd like to go. You can always get the full report later, after you see your free snapshot.</p>

      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        {[
          { key: "free", title: "Free Snapshot", price: "$0", desc: "A shorter set of questions and your score, with one strength, one thing to watch, and one next step." },
          { key: "focus", title: "Focus Report", price: `$${config?.pricing?.tiers?.focus?.usd ?? 29}`, desc: "Your category scores, top strengths and gaps, and the funding paths that fit you best." },
          { key: "full", title: "Full Report", price: `$${config?.pricing?.tiers?.full?.usd ?? 79}`, desc: "Everything in Focus, plus a draft funding story, a 90-day plan, a PDF, and email delivery." },
        ].map((o) => (
          <button key={o.key} onClick={() => setTier(o.key)} data-testid={`tier-${o.key}`}
            className={`text-left p-6 rounded-md border-2 transition ${tier === o.key ? "border-clay bg-white" : "border-line bg-white hover:border-clay/50"}`}>
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-ink text-lg">{o.title}</span>
              <span className="font-display font-extrabold text-ink">{o.price}</span>
            </div>
            <p className="mt-2 text-sm text-ink/70 leading-relaxed">{o.desc}</p>
          </button>
        ))}
      </div>

      {!user && (
        <div className="mt-6">
          <label className="text-sm font-medium text-navy">Email (optional — to save & email your results)</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@organization.org"
            className="mt-2 w-full px-4 py-3 bg-white border border-line rounded-[3px] focus:ring-2 focus:ring-gold outline-none" data-testid="start-email" />
        </div>
      )}

      <div className="mt-6 space-y-3">
        <label className="flex items-start gap-3 text-sm text-charcoal/80 cursor-pointer">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 accent-[#C5A059]" data-testid="agree-terms" />
          <span>I agree to the <a href="/terms" className="text-navy font-semibold">Terms of Use</a> and understand that results do not guarantee funding.</span>
        </label>
        <label className="flex items-start gap-3 text-sm text-charcoal/80 cursor-pointer">
          <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} className="mt-1 accent-[#C5A059]" data-testid="agree-authorized" />
          <span>I confirm I am authorized to submit organizational information and any documents I choose to upload.</span>
        </label>
      </div>

      <button onClick={start} disabled={loading || !agreed} data-testid="begin-assessment"
        className="mt-8 btn-gold font-semibold px-8 py-4 rounded-[3px] flex items-center gap-2 disabled:opacity-50">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <>Begin assessment <ArrowRight size={18} /></>}
      </button>
    </div>
  );
}

/* ---------- Flow ---------- */
export function AssessmentFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const config = usePublicConfig();
  const { user } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [profile, setProfile] = useState({});
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState("");
  const loaded = useRef(false);

  useEffect(() => {
    api.get(`/assessments/${id}`).then((r) => {
      setAssessment(r.data);
      setProfile(r.data.profile || {});
      setAnswers(r.data.answers || {});
      setStep(r.data.current_step || 0);
      loaded.current = true;
    }).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Not found"));
  }, [id]);

  const tier = assessment?.tier || "full";
  const questions = (config?.questions || []).filter((q) => tier !== "free" || q.free);
  const catList = (config?.categories || []).filter((c) => questions.some((q) => q.category === c.key));
  const profileFields = config?.profile_fields || [];

  // steps: 0 = profile, 1..N = categories, then documents (full only)
  const steps = ["profile", ...catList.map((c) => c.key), ...(tier !== "free" ? ["documents"] : [])];
  const totalSteps = steps.length;
  const currentKey = steps[step];

  const save = useCallback(async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/assessments/${id}`, patch);
    } catch (e) { console.warn("Autosave failed (will retry on next change)", e); }
    finally { setSaving(false); }
  }, [id]);

  const next = async () => {
    // validation for profile step
    if (currentKey === "profile") {
      const missing = profileFields.filter((f) => f.required && !String(profile[f.key] || "").trim());
      if (missing.length) { toast.error(`Please complete: ${missing.map((m) => m.label).join(", ")}`); return; }
    }
    const newStep = Math.min(step + 1, totalSteps - 1);
    setStep(newStep);
    await save({ profile, answers, current_step: newStep });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep(Math.max(0, step - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const complete = async () => {
    setCompleting(true);
    try {
      await save({ profile, answers, current_step: step });
      await api.post(`/assessments/${id}/complete`);
      track("assessment_complete", id);
      gaEvent("assessment_completed", { assessment_id: id, tier });
      navigate(`/results/${id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCompleting(false);
    }
  };

  if (error) return <div className="max-w-2xl mx-auto px-5 py-24 text-center text-muted-foreground">{error}</div>;
  if (!assessment || !config) return <div className="py-24 text-center text-muted-foreground"><Loader2 className="animate-spin mx-auto" /></div>;

  const progress = Math.round(((step) / (totalSteps - 1)) * 100);
  const isLast = step === totalSteps - 1;

  const progressMessage = () => {
    if (currentKey === "profile") return "First, a little about your organization.";
    if (currentKey === "documents") return "Last step — share anything that helps. Totally optional.";
    const map = {
      story: "Now, how you tell your story.",
      evidence: "Next, the evidence behind your work.",
      revenue: "Let's look at how you bring in money.",
      relationship: "Now, your donor and funder relationships.",
      infrastructure: "Almost there — a few on leadership and systems.",
    };
    return map[currentKey] || "You're doing great — keep going.";
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12">
      {/* progress */}
      <div className="mb-8">
        <p className="font-display text-lg font-semibold text-ink mb-3">{progressMessage()}</p>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span className="font-medium">Step {step + 1} of {totalSteps}</span>
          <span>{saving ? "Saving…" : "Saved — you can come back anytime"}</span>
        </div>
        <div className="h-1.5 bg-line rounded-full overflow-hidden">
          <div className="h-full bg-clay rounded-full" style={{ width: `${progress}%`, transition: "width 0.4s ease" }} />
        </div>
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {steps.map((s, i) => (
            <span key={s} className={`h-1.5 flex-1 min-w-[24px] rounded-full ${i <= step ? "bg-green" : "bg-line"}`} />
          ))}
        </div>
      </div>

      <div key={currentKey} className="animate-fade-up">
        {currentKey === "profile" && (
          <ProfileStep fields={profileFields} profile={profile} setProfile={setProfile} />
        )}
        {catList.find((c) => c.key === currentKey) && (
          <CategoryStep category={catList.find((c) => c.key === currentKey)}
            questions={questions.filter((q) => q.category === currentKey)}
            answers={answers} setAnswers={setAnswers} />
        )}
        {currentKey === "documents" && (
          <DocumentsStep assessmentId={id} config={config} assessment={assessment} refresh={() => api.get(`/assessments/${id}`).then((r) => setAssessment(r.data))} />
        )}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button onClick={back} disabled={step === 0} data-testid="assessment-back"
          className="flex items-center gap-2 text-charcoal/70 hover:text-navy disabled:opacity-30 font-medium">
          <ArrowLeft size={18} /> Back
        </button>
        {isLast ? (
          <button onClick={complete} disabled={completing} data-testid="assessment-complete"
            className="btn-gold font-semibold px-8 py-3.5 rounded-[3px] flex items-center gap-2 disabled:opacity-60">
            {completing ? <Loader2 className="animate-spin" size={18} /> : <>See my results <ArrowRight size={18} /></>}
          </button>
        ) : (
          <button onClick={next} data-testid="assessment-next"
            className="btn-gold font-semibold px-8 py-3.5 rounded-[3px] flex items-center gap-2">
            Continue <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Steps ---------- */
const label = "block text-sm font-medium text-ink mb-2";
const inputC = "w-full px-4 py-3 bg-white border border-line rounded-[3px] focus:ring-2 focus:ring-gold focus:border-gold outline-none transition";

function ProfileStep({ fields, profile, setProfile }) {
  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  return (
    <div>
      <h2 className="text-2xl font-bold text-ink font-display">A little about your organization</h2>
      <p className="mt-2 text-ink/70">This helps the report speak to your actual situation, not a generic one.</p>
      <div className="mt-8 space-y-5">
        {fields.map((f) => (
          <div key={f.key}>
            <label className={label} htmlFor={f.key}>{f.label}{f.required && <span className="text-copper"> *</span>}</label>
            {f.type === "textarea" ? (
              <textarea id={f.key} rows={3} className={inputC} value={profile[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`profile-${f.key}`} />
            ) : f.type === "select" ? (
              <select id={f.key} className={inputC} value={profile[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`profile-${f.key}`}>
                <option value="">Select…</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === "yes_no" ? (
              <div className="flex gap-2">
                {["Yes", "Partially", "No"].map((o) => (
                  <button key={o} type="button" onClick={() => set(f.key, o)} data-testid={`profile-${f.key}-${o}`}
                    className={`px-5 py-2.5 rounded-[3px] border text-sm font-medium transition ${profile[f.key] === o ? "bg-green text-cream border-green" : "bg-white border-line text-ink/70 hover:border-clay"}`}>{o}</button>
                ))}
              </div>
            ) : (
              <input id={f.key} className={inputC} value={profile[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`profile-${f.key}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryStep({ category, questions, answers, setAnswers }) {
  const set = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  return (
    <div>
      <span className="text-clay text-xs font-semibold tracking-[0.2em] uppercase">A few questions</span>
      <h2 className="mt-2 text-2xl font-bold text-ink font-display">{category.name}</h2>
      <p className="mt-2 text-ink/70">{category.description}</p>
      <div className="mt-8 space-y-7">
        {questions.map((q) => (
          <div key={q.id} className="p-5 bg-white border border-line rounded-md">
            <p className="font-medium text-ink leading-snug">{q.text}</p>
            <div className="mt-4">
              {q.type === "text" ? (
                <textarea rows={3} className={inputC} placeholder="Your answer (optional)"
                  value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} data-testid={`q-${q.id}`} />
              ) : (
                <div className="flex flex-wrap gap-2" data-testid={`q-${q.id}`}>
                  {q.options.map((o) => (
                    <button key={o.label} type="button" onClick={() => set(q.id, o.value)}
                      data-testid={`q-${q.id}-${o.value}`}
                      className={`px-4 py-2.5 rounded-[3px] border text-sm font-medium transition ${String(answers[q.id]) === String(o.value) ? "bg-green text-cream border-green" : "bg-white border-line text-ink/70 hover:border-clay"}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocumentsStep({ assessmentId, config, assessment, refresh }) {
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState((config.document_types || [])[0] || "Document");
  const docs = assessment.documents || [];

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      await api.post(`/assessments/${assessmentId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="text-clay text-xs font-semibold tracking-[0.2em] uppercase">Optional</span>
      </div>
      <h2 className="mt-2 text-2xl font-bold text-ink font-display">Anything you'd like to share?</h2>
      <p className="mt-2 text-ink/70 leading-relaxed">
        These are optional and only used to sharpen your report. PDF, DOCX, and common image files work. Skip this
        entirely if you'd rather — it won't hold anything up.
      </p>

      <div className="mt-6 p-5 bg-white border border-line rounded-md">
        <label className={label}>Document type</label>
        <select className={inputC} value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type">
          {(config.document_types || []).map((d) => <option key={d}>{d}</option>)}
        </select>
        <label className="mt-4 flex items-center justify-center gap-2 border-2 border-dashed border-line rounded-md py-8 cursor-pointer hover:border-gold transition" data-testid="doc-dropzone">
          {uploading ? <Loader2 className="animate-spin text-gold" /> : <Upload className="text-gold" />}
          <span className="text-sm text-charcoal/70">{uploading ? "Uploading…" : "Click to choose a file"}</span>
          <input type="file" className="hidden" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp,.gif"
            onChange={(e) => upload(e.target.files[0])} data-testid="doc-input" />
        </label>
      </div>

      {docs.length > 0 && (
        <div className="mt-5 space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 bg-ivory border border-line rounded-[3px]">
              <FileText size={18} className="text-navy" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-navy truncate">{d.filename}</p>
                <p className="text-xs text-muted-foreground">{d.doc_type} · {(d.size / 1024).toFixed(0)} KB</p>
              </div>
              <Check size={16} className="text-forest" />
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-copper" />
        <span>Your documents are private, stored securely, and never publicly exposed.</span>
      </div>
    </div>
  );
}
